import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { forOwn, isBoolean, map } from 'lodash';

import { LocaleService, WindowProxy } from '@sm360/widget-commons-core-services';
import { AnalyticsService, VIRTUAL_PAGEVIEWS as PAGES } from '@sm360/widget-commons-general';
import { FormBuilder, ValidationService, ErrorsHandler } from '@sm360/widget-commons-forms';

import FORM_INPUTS from 'trade-in/components/OfficialTradeInForm/components/CarStory/formInputs.js';

import 'trade-in/components/OfficialTradeInForm/components/CarStory/CarStory.scss';

class CarStory extends Component {
    constructor(props) {
        super(props);

        const inputs = [
            'originalOwner',
            'vehiclePurchase',
            'vehicleCondition',
            'corporateUse',
            'corporateUseExplanation',
            'rustProof',
            'mechanicalProtection',
            'coverageBalance',
        ].reduce((accum, keyName) => {
            accum[keyName] = props.defaultInputs[keyName] || '';
            return accum;
        }, {});

        this.state = {
            inputs,
            formValidation: {
                originalOwner: false,
                vehiclePurchase: false,
                vehicleCondition: false,
                corporateUse: false,
                corporateUseExplanation: true,
                rustProof: false,
                mechanicalProtection: false,
                coverageBalance: true,
            },
            errors: [],
        };

        this.buildInput = this.buildInput.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.sendForm = this.sendForm.bind(this);
        this.validateForm = this.validateForm.bind(this);
    }

    componentDidMount() {
        AnalyticsService.virtualPageView(PAGES.OFFICIAL_TRADEIN_CAR_STORY());
    }

    validateForm() {
        const { formValidation } = this.state;

        forOwn(formValidation, (value, key) => {
            const fieldRequireValidation =
                (key === 'corporateUseExplanation' && this.state.inputs.corporateUse) ||
                (key === 'coverageBalance' && this.state.inputs.mechanicalProtection) ||
                (key !== 'coverageBalance' && key !== 'corporateUseExplanation');

            if (fieldRequireValidation) {
                const stateValue = this.state.inputs[key];
                formValidation[key] = isBoolean(stateValue) || ValidationService.validateIsNotEmpty(stateValue);
            } else {
                formValidation[key] = true;
            }
        });

        this.setState({ formValidation });
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    handleInputChange(value, name) {
        const nextState = this.state;

        nextState.inputs[name] = value;
        nextState.formValidation[name] = true;

        this.setState(nextState, this.validateForm);
    }

    sendForm(event) {
        event.preventDefault();
        if (!ValidationService.validateForm(this.state.formValidation)) {
            this.setState({
                errors: [
                    {
                        message: LocaleService.t('TRADE_IN.VEHICLE_STORY.ERROR'),
                        code: 'error.missing_inputs',
                        group: 'carStory',
                    },
                ],
            });
            const nav = WindowProxy.get().document.getElementsByClassName('officialTradeInForm')
                ? WindowProxy.get().document.getElementsByClassName('officialTradeInForm')[0]
                : undefined;
            if (nav) {
                nav.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            return;
        }

        this.setState({
            errors: [],
        });

        const data = { ...this.state.inputs };
        this.props.onFormSubmit(data);
    }

    buildInput(input) {
        const options =
            input.options &&
            input.options.map((option) => {
                return {
                    ...option,
                    label: <span>{LocaleService.t(option.transKey)}</span>,
                    isRequired: true,
                };
            });

        return (
            <div key={input.name}>
                <FormBuilder
                    inputParams={{
                        ...input,
                        label: LocaleService.t(input.transKey),
                        options,
                        value: this.state.inputs[input.name],
                        isValid: this.state.formValidation[input.name],
                    }}
                    onChange={this.handleInputChange}
                />
                {input.subQuestions && this.state.inputs[input.name] && input.subQuestions.map(this.buildInput)}
            </div>
        );
    }

    render() {
        const { isOpen } = this.props;
        const inputs = map(FORM_INPUTS, this.buildInput);

        const formClass = isOpen
            ? 'officialTradeIn__section carStory is-open'
            : 'officialTradeIn__section carStory is-closed is-valid';

        const errorBlock = !!this.state.errors.length && <ErrorsHandler errors={this.state.errors} />;

        const carStoryForm = isOpen && (
            <form onSubmit={this.handleSubmit}>
                {inputs}
                <button type="button" className="sr-button-1" onClick={this.sendForm}>
                    {LocaleService.t('TRADE_IN.BUTTONS.NEXT')}
                </button>
            </form>
        );

        return (
            <div className={formClass}>
                <p className="sr-text is-bold">{LocaleService.t('TRADE_IN.VEHICLE_STORY.TITLE')}</p>
                {errorBlock}
                {carStoryForm}
            </div>
        );
    }
}

CarStory.propTypes = {
    isOpen: PropTypes.bool,
    defaultInputs: PropTypes.objectOf({}),
    onFormSubmit: PropTypes.func.isRequired,
};

CarStory.defaultProps = {
    isOpen: true,
    defaultInputs: {},
};

CarStory.displayName = 'CarStory';

export default CarStory;
